'use client';

import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { usePathname } from 'next/navigation';
import { Link } from '@/i18n/navigation';
import styles from './Navigation.module.scss';
import Button, { ButtonType } from '../Button/Button';
import Icon from '../Icon';
import Img from '../Img';
import LanguageDropdown, { Locales } from './LanguageDropdown/LanguageDropdown';
import { useResize } from '@/hooks/useResize';
import NavigationItemMobile from './NavItemMobile';
import NavigationItemDesktop from './NavigationItemDesktop';
import { INavigationItem } from './Navigation.type';

export interface NavigationProps {
  logo: {
    height: number;
    width: number;
    url: string;
    name: string;
  };
  logoDark: {
    height: number;
    width: number;
    url: string;
    name: string;
  };
  items: INavigationItem[];
  button: {
    id: number;
    link: string;
    title: string;
    type: ButtonType;
    target: '_self' | '_target'
  }
}

const SHOW_LOGIN_BUTTON = true;
const SHOW_LANGUAGE_SWITCHER = true;

const Navigation = ({ data, locales }: { data: NavigationProps, locales: Locales[] }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openSubmenuId, setOpenSubmenuId] = useState<number | null>(null);
  const screenWidth = useResize();
  const pathname = usePathname();
  const [isHomePage, setIsHomePage] = useState(pathname.split('/').length === 2);

  useEffect(() => {
    setIsHomePage(pathname.split('/').length === 2);
  }, [pathname]);

  useEffect(() => {
    if (screenWidth > 768) {
      setIsMobileMenuOpen(false);
    }
  }, [screenWidth]);

  useEffect(() => {
    document.documentElement.style.overflow = isMobileMenuOpen ? 'hidden' : 'unset';
  }, [isMobileMenuOpen]);

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const toggleMenu = (id: number) => {
    setOpenSubmenuId((prev) => (prev === id ? null : id));
  };

  return (
    <nav className={clsx(styles.navigation, {
      [styles.notHomePage]: !isHomePage,
      [styles.mobileMenuOpen]: isMobileMenuOpen,
    })}
    >
      <div className={styles.container}>
        {data.logo ? (
          <Link
            href='/'
          >
            <Img
              src={isHomePage || isMobileMenuOpen ? data.logo.url : data.logoDark.url}
              alt={data.logo.name}
              width={data.logo.width}
              height={data.logo.height}
            />
          </Link>
        ) : <div />}

        <div className={styles.categories}>
          {data.items && data.items.map((item) => (
            <NavigationItemDesktop
              key={item.id}
              item={item}
            />
          ))}
        </div>

        <div className={styles.rightSection}>
          {data.button && SHOW_LOGIN_BUTTON && (
            <Button
              type={(isHomePage || isMobileMenuOpen) ? data.button.type : ButtonType.SECONDARY_LIGHT}
              className={styles.accountButton}
              href={data.button.link}
              target={data.button?.target}
              targetBlank
            >
              {data.button.title}
            </Button>
          )}
          {SHOW_LANGUAGE_SWITCHER && (
            <LanguageDropdown
              isLight={isHomePage || isMobileMenuOpen}
              locales={locales}
            />
          )}
        </div>
        <div className={styles.burgerContainer}>
          <Button
            type={(isHomePage || isMobileMenuOpen) ? ButtonType.SECONDARY_DARK : ButtonType.SECONDARY_LIGHT}
            className={styles.burgerButton}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <Icon name='menu' />
          </Button>
        </div>
      </div>
      {isMobileMenuOpen && (
        <div className={styles.mobileMenu}>
          <div className={styles.mobileContent}>
            <div />
            <div className={styles.mobileCategories}>
              {data.items && data.items.map((item) => (
                <NavigationItemMobile
                  key={item.id}
                  item={item}
                  closeMenu={closeMobileMenu}
                  isSubmenuOpen={openSubmenuId === item.id}
                  toggleMenu={() => toggleMenu(item.id)}
                />
              ))}
            </div>
            <div className={styles.mobileRightSection}>
              {data.button && SHOW_LOGIN_BUTTON && (
                <div>
                  <Button
                    type={data.button.type}
                    className={styles.accountButton}
                    href={data.button.link}
                    target={data.button?.target}
                    targetBlank
                  >
                    {data.button.title}
                  </Button>
                </div>
              )}
              {SHOW_LANGUAGE_SWITCHER && (
                <LanguageDropdown
                  isLight={isHomePage || isMobileMenuOpen}
                  locales={locales}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
